import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ArrowRight } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { Card, Empty } from '../../components/ui/Card.jsx';
import { useTenant } from '../../components/TenantContext.jsx';
import { fetchAllOwnedIvrAvatars } from '../../api/ivrAgents.js';

/**
 * SurveyDataIndexPage — 列出所有 survey avatars
 *
 *   0 個 → 「尚未有問卷」empty
 *   1 個 → auto navigate /survey-data/:avatarId
 *   N 個 → 卡片列表
 */
export default function SurveyDataIndexPage() {
    const { t } = useTranslation('survey');
    const { hostId } = useTenant();
    const navigate = useNavigate();
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await fetchAllOwnedIvrAvatars({ refresh: false, hostId });
                if (cancelled) return;
                const surveyAvatars = (list || []).filter((a) => a.newMode === 'survey' || a.surveyMode === true);
                setAvatars(surveyAvatars);
                // 只有 1 個 → auto redirect
                if (surveyAvatars.length === 1) {
                    navigate(`/survey-data/${surveyAvatars[0].avatarId}`, { replace: true });
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [hostId, navigate]);

    return (
        <div>
            <PageHeader
                eyebrow={hostId ? `tenant · ${hostId}` : 'tenant'}
                title={t('index.title')}
                description={t('index.description')}
            />

            {loading ? (
                <Card>
                    <div className="text-sm text-ink-400 italic text-center py-6">{t('index.loadingList')}</div>
                </Card>
            ) : error ? (
                <Card className="bg-danger-100/40 border-danger-500/30">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            ) : avatars.length === 0 ? (
                <Card>
                    <Empty
                        icon={ClipboardList}
                        title={t('index.emptyTitle')}
                        description={t('index.emptyDesc')}
                    />
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {avatars.map((a) => (
                        <Card
                            key={a.avatarId}
                            padded={false}
                            className="hover:border-accent-300 transition cursor-pointer"
                        >
                            <div className="px-5 py-4" onClick={() => navigate(`/survey-data/${a.avatarId}`)}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="text-base font-semibold text-ink-900 truncate">{a.name || t('untitled')}</h3>
                                        {a.scenario && (
                                            <p className="mt-2 text-xs text-ink-500 line-clamp-2">{a.scenario}</p>
                                        )}
                                    </div>
                                    <ArrowRight size={16} className="text-ink-400 shrink-0 mt-1" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
